import {
  NodeDataType,
  NodeEventType,
  WorkflowEventTypes,
  type NodeEvent,
  type NodeStatusEvent,
} from '@/types/workflow-types';

/**
 * Per-node status flags derived from the WS event log.
 *
 * Single-pass walk over ``nodeStatusEvents`` so the four flags stay
 * consistent under the ``RETRY_SCHEDULED`` interleaving (a node can
 * legitimately go begin -> processing -> emit_error -> retry_scheduled
 * -> begin -> ... -> execution_successful and we need each flag to
 * reflect the most recent meaningful signal, not a stale earlier one).
 *
 * Why this is its own helper:
 *
 *   * The ``RETRY_SCHEDULED`` event landed between two existing
 *     terminal-class events (``EXECUTION_FAILED`` and
 *     ``EXECUTION_SUCCESSFUL``); the supersedes-logic isn't a clean
 *     ``some()`` -- it requires knowing the relative ordering of the
 *     last terminal failure, the last retry, and the last
 *     non-terminal/non-retry event.
 *   * Pure, deterministic, and trivial to test exhaustively without
 *     mounting the editor.
 *
 * Caller (``nodesWithStatuses`` memo in workflow-editor-page.tsx)
 * combines these with ``isExecutingWorkflow`` and other context flags
 * (``isPausedWaiting``, ``isCached``, etc.) that aren't event-driven.
 */
export interface DerivedNodeStatus {
  /** A successful terminal event has landed for this node. */
  isCompleted: boolean;
  /**
   * The most recent terminal-failure event is NOT superseded by a
   * later retry / success / new-attempt event.  Without this
   * supersedes-check the node turns red on the first attempt's
   * emit_error and stays red even when a retry succeeds.
   */
  hasError: boolean;
  /**
   * The most recent event is ``RETRY_SCHEDULED`` and no later
   * begin/processing/terminal event has come back yet.  Drives the
   * amber retrying badge in GenericNode.
   */
  isRetrying: boolean;
  /**
   * The node has emitted at least one event AND has no terminal
   * outcome.  ``RETRY_SCHEDULED`` keeps this true (the next attempt
   * will come along shortly).
   */
  isExecutingFromEvents: boolean;
}

/**
 * Compute per-node status flags from the raw event log.
 *
 * ``isExecutingFromEvents`` does NOT include the workflow-level
 * "is the run currently executing" check -- callers AND that with
 * their own ``isExecutingWorkflow`` because status events alone
 * can't distinguish "node finished a past run with no terminal
 * event" from "node is currently in flight in the live run".
 */
export function deriveNodeStatusFromEvents(
  nodeStatusEvents: readonly NodeStatusEvent[],
): DerivedNodeStatus {
  let isCompleted = false;
  let hasError = false;
  let isRetrying = false;

  let lastTerminalIndex = -1;
  let lastRetryIndex = -1;
  let lastSuccessIndex = -1;
  let lastNonRetryNonTerminalIndex = -1;

  for (let i = 0; i < nodeStatusEvents.length; i++) {
    const event = nodeStatusEvents[i];
    if (event.event_type === WorkflowEventTypes.LIVE_TRANSCRIPT) {
      continue;
    }
    const nodeEvent = event as NodeEvent;
    const et = nodeEvent.event_type;
    const dt = nodeEvent.display_type;
    const isTerminalFailure =
      et === NodeEventType.EXECUTION_FAILED || dt === NodeDataType.ERROR;
    // ZZZ: legacy quirk -- the Human Intervention node uses ``end``
    // as its successful-completion marker instead of execution_successful.
    // Removed once the BE switches the human-intervention node to the
    // standard event vocabulary.
    const isHumanInterventionEnd =
      nodeEvent.node_display_name === 'Human Intervention' &&
      et === NodeEventType.END;
    const isSuccessLike =
      et === NodeEventType.EXECUTION_SUCCESSFUL || isHumanInterventionEnd;

    if (isSuccessLike) {
      isCompleted = true;
      lastSuccessIndex = i;
      lastTerminalIndex = i;
    } else if (isTerminalFailure) {
      lastTerminalIndex = i;
    } else if (et === NodeEventType.RETRY_SCHEDULED) {
      lastRetryIndex = i;
    } else {
      lastNonRetryNonTerminalIndex = i;
    }
  }

  // hasError: a terminal failure event is "real" only if nothing
  // later supersedes it (a retry, a successful re-attempt, or the
  // next attempt's begin/processing).
  if (lastTerminalIndex >= 0 && lastTerminalIndex !== lastSuccessIndex) {
    const supersededByRetry = lastRetryIndex > lastTerminalIndex;
    const supersededByActivity =
      lastNonRetryNonTerminalIndex > lastTerminalIndex;
    const supersededBySuccess = lastSuccessIndex > lastTerminalIndex;
    hasError =
      !supersededByRetry && !supersededByActivity && !supersededBySuccess;
  }

  // isRetrying: most recent event is the retry marker AND nothing
  // later has arrived.  Once the next attempt emits its begin /
  // processing the FE flips back to the executing visual.
  if (
    lastRetryIndex >= 0 &&
    lastRetryIndex > lastTerminalIndex &&
    lastRetryIndex > lastNonRetryNonTerminalIndex
  ) {
    isRetrying = true;
  }

  // Executing-from-events: the node has emitted a real lifecycle event
  // (not just live_transcript deltas) and we don't have a terminal
  // outcome that's been allowed to stand.
  const hasNonLiveEvent = nodeStatusEvents.some(
    (event) => event.event_type !== WorkflowEventTypes.LIVE_TRANSCRIPT,
  );
  const isExecutingFromEvents = hasNonLiveEvent && !isCompleted && !hasError;

  return { isCompleted, hasError, isRetrying, isExecutingFromEvents };
}
